import { reactive, watch, UnwrapNestedRefs } from 'vue'

import {
  FilePickerProps,
  FilePickerSetupContext,
} from '@/components/filepicker/types'

import mineTypes from '../utils/minetypes'

interface UseConfigProps {
  props: FilePickerProps
  context: FilePickerSetupContext
  setMultiple: (newValue: boolean) => void
}

export type UseConfig = UnwrapNestedRefs<
  Pick<
    FilePickerProps,
    | 'maxFiles'
    | 'maxFileSize'
    | 'hiddenInputContainer'
    | 'clickable'
    | 'acceptedFiles'
    | 'dropzoneMessageClassName'
  > & {
    accepts: string[]
  }
>

export interface UseConfigReturn {
  config: UseConfig
}

export default function useConfig({
  props,
  context,
  setMultiple,
}: UseConfigProps): UseConfigReturn {
  // Dropbox reactive config
  const config: UseConfig = reactive({
    maxFiles: props.maxFiles,
    maxFileSize: props.maxFileSize,
    hiddenInputContainer: props.hiddenInputContainer,
    clickable: props.clickable,
    acceptedFiles: props.acceptedFiles,
    dropzoneMessageClassName: props.dropzoneMessageClassName,
    accepts: [],
  })

  const createAcceptsArray = () => {
    const acceptsTmp: string[] = []
    // CREATE THE ACCEPTS ARRAY
    if (props.acceptedFiles !== null) {
      props.acceptedFiles.forEach((accept) => {
        mineTypes.data
          .filter(
            (mt) =>
              (mt.ext &&
                !!mt.ext
                  .split(/\s/)
                  .find((extension) => accept === extension)) ||
              mt.mime_type.startsWith(accept),
          )
          .forEach((mdType) => {
            acceptsTmp.push(mdType.mime_type)
            if (mdType.ext) {
              acceptsTmp.push(...mdType.ext.split(/\s/).map((e) => `.${e}`))
            }
          })
      })
    }
    config.accepts = [...acceptsTmp]
  }

  const emitConfigUpdate = () => {
    context.emit('config-update', { ...config })
  }

  createAcceptsArray()
  emitConfigUpdate()
  watch(
    () => props.acceptedFiles,
    (val) => {
      if (!config.acceptedFiles.every((accept) => val.includes(accept))) {
        config.acceptedFiles = [...val]
        createAcceptsArray()
        emitConfigUpdate()
      }
    },
  )
  watch(
    () => props.maxFiles,
    (val) => {
      if (config.maxFiles !== val) {
        config.maxFiles = val
        emitConfigUpdate()
      }
    },
  )
  watch(
    () => props.maxFileSize,
    (val) => {
      if (config.maxFileSize !== val) {
        config.maxFileSize = val
        emitConfigUpdate()
        if (config.maxFileSize > 1) {
          setMultiple(true)
        } else {
          setMultiple(false)
        }
      }
    },
  )
  watch(
    () => props.hiddenInputContainer,
    (val) => {
      if (config.hiddenInputContainer !== val) {
        config.hiddenInputContainer = val
        emitConfigUpdate()
      }
    },
  )
  watch(
    () => props.clickable,
    (val) => {
      if (config.clickable !== val) {
        config.clickable = val
        emitConfigUpdate()
      }
    },
  )
  return { config }
}
